import { SYNTH, renderSynth } from './synth'

/** Every sound effect (PHASE3 T3.6); each has a synthesized placeholder in `synth.ts`. */
export const SFX_IDS = [
  'click',
  'jump',
  'double_jump',
  'land',
  'pickup',
  'item_use',
  'item_boost_jet',
  'item_shield_bubble',
  'item_trip_wire',
  'item_spike_ball',
  'item_homing_drone',
  'item_oil_slick',
  'item_leader_missile',
  'item_magnet',
  'hit',
  'shield_pop',
  'shard',
  'checkpoint',
  'respawn',
  'countdown_tick',
  'go',
  'finish',
  'podium',
  'reward_tick',
  'ride_summon',
  'ride_end',
] as const
export type SfxId = (typeof SFX_IDS)[number]

/** Music loops: files only (no synthesized music); a missing file is silence. */
export const MUSIC_IDS = ['music_hub', 'music_meadow'] as const
export type MusicId = (typeof MUSIC_IDS)[number]

export type SoundId = SfxId | MusicId

/** The race music by track theme; one world so far, so every theme gets the meadow loop. */
const THEME_MUSIC: Readonly<Record<string, MusicId>> = { sky_meadow: 'music_meadow' }

export function musicForTheme(theme: string | undefined): MusicId {
  return THEME_MUSIC[theme ?? ''] ?? 'music_meadow'
}

/** File name under `public/assets/audio/` (its README lists them): `music_*.mp3`, `sfx_<id>.mp3`. */
export function soundFile(id: SoundId): string {
  return id.startsWith('music_') ? `${id}.mp3` : `sfx_${id}.mp3`
}

/** The part of a `BaseAudioContext` the bank needs. */
export interface BufferMaker<B> {
  readonly sampleRate: number
  createBuffer(channels: number, length: number, sampleRate: number): B
  decodeAudioData(data: ArrayBuffer): Promise<B>
}

/** Writes samples into a buffer made by `createBuffer` (`AudioBuffer.copyToChannel`). */
export type FillBuffer<B> = (buffer: B, samples: Float32Array) => void

/** The part of `fetch` the bank needs. */
export type Fetcher = (
  url: string,
  init?: { method: string },
) => Promise<{
  ok: boolean
  headers: { get(name: string): string | null }
  arrayBuffer(): Promise<ArrayBuffer>
}>

/**
 * Buffers by sound id: a real file when one exists (one HEAD request per id, cached; an SPA
 * fallback page does not count), else the synthesized placeholder for SFX and nothing for music.
 */
export class SoundBank<B> {
  private readonly files = new Map<SoundId, Promise<B | null>>()
  private readonly ready = new Map<SoundId, B | null>()
  private readonly synth = new Map<SfxId, B>()

  constructor(
    private readonly ctx: BufferMaker<B>,
    private readonly fill: FillBuffer<B>,
    private readonly fetcher: Fetcher | null,
    private readonly baseUrl: string,
  ) {}

  /** Look for the file once; resolves to its decoded buffer, or null when there is none. */
  load(id: SoundId): Promise<B | null> {
    let p = this.files.get(id)
    if (!p) {
      p = this.fetchFile(id).then((b) => {
        this.ready.set(id, b)
        return b
      })
      this.files.set(id, p)
    }
    return p
  }

  /** The buffer to play for an effect now: its file when loaded, else the placeholder. */
  sfx(id: SfxId): B {
    const file = this.ready.get(id)
    if (file) return file
    if (!this.files.has(id)) void this.load(id)
    let b = this.synth.get(id)
    if (!b) {
      const samples = renderSynth(SYNTH[id], this.ctx.sampleRate)
      b = this.ctx.createBuffer(1, Math.max(1, samples.length), this.ctx.sampleRate)
      this.fill(b, samples)
      this.synth.set(id, b)
    }
    return b
  }

  private async fetchFile(id: SoundId): Promise<B | null> {
    if (!this.fetcher) return null
    const url = this.baseUrl + soundFile(id)
    try {
      const head = await this.fetcher(url, { method: 'HEAD' })
      const type = head.headers.get('content-type') ?? ''
      if (!head.ok || type.includes('text/html')) return null
      const res = await this.fetcher(url)
      if (!res.ok) return null
      return await this.ctx.decodeAudioData(await res.arrayBuffer())
    } catch {
      return null
    }
  }
}
